import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { agencies as staticAgencies } from "../data/content";

const STORAGE_KEY = "epp_selected_store_id";
const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [selectedId, setSelectedIdState] = useState(() => {
    if (typeof globalThis.window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY) || null;
  });

  const [storesFromApi, setStoresFromApi] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v2/stores?active_only=true`);
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) {
            setStoresFromApi(data.map((s) => ({
              id: s.id,
              name: s.name,
              slug: s.slug || "",
              city: s.city || "",
              address: [s.address, s.postal_code, s.city].filter(Boolean).join(", "),
              rawAddress: s.address || "",
              postalCode: s.postal_code || "",
              phone: s.phone || "",
              hours: s.opening_hours || null,
              showroomHours: s.showroom_hours || null,
              locationType: s.location_type || "store",
              supportsClickCollect: s.supports_click_collect !== false,
              supportsDelivery: s.supports_delivery || false,
              isShowroom: s.location_type === "showroom",
              isMain: s.is_main || false,
              exceptionalClosure: s.exceptional_closure || null,
              description: s.description || "",
              image: s.image || "",
              email: s.email || "",
              lat: s.lat,
              lng: s.lng,
            })));
          }
        }
      } catch { /* fallback to static */ }
      finally { setLoaded(true); }
    })();
  }, []);

  const agenciesList = storesFromApi.length > 0 ? storesFromApi : staticAgencies;

  const setSelectedId = useCallback((id) => {
    setSelectedIdState(id);
    if (typeof globalThis.window !== "undefined") {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const [suggestedStoreId, setSuggestedStoreId] = useState(null);

  const autoSuggestNearest = useCallback(() => {
    if (selectedId || !loaded || agenciesList.length === 0) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const toRad = (v) => (v * Math.PI) / 180;
        let best = null;
        let bestDist = Infinity;
        for (const s of agenciesList) {
          if (s.lat == null || s.lng == null) continue;
          if (s.locationType === "showroom" || s.isShowroom) continue;
          if (s.supportsClickCollect === false) continue;
          const dlat = toRad(Number(s.lat) - latitude);
          const dlng = toRad(Number(s.lng) - longitude);
          const a = Math.sin(dlat / 2) ** 2 + Math.cos(toRad(latitude)) * Math.cos(toRad(Number(s.lat))) * Math.sin(dlng / 2) ** 2;
          const d = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          if (d < bestDist) { bestDist = d; best = s; }
        }
        if (best) setSuggestedStoreId(best.id);
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, [selectedId, loaded, agenciesList]);

  useEffect(() => {
    if (loaded && !selectedId) autoSuggestNearest();
  }, [loaded, selectedId, autoSuggestNearest]);

  const selectedStore = selectedId ? agenciesList.find((a) => a.id === selectedId) : null;
  const suggestedStoreRaw = suggestedStoreId ? agenciesList.find((a) => a.id === suggestedStoreId) : null;
  const suggestedStore =
    suggestedStoreRaw &&
    !suggestedStoreRaw.isShowroom &&
    suggestedStoreRaw.locationType !== "showroom" &&
    suggestedStoreRaw.supportsClickCollect !== false
      ? suggestedStoreRaw
      : null;

  useEffect(() => {
    if (
      suggestedStoreId &&
      suggestedStoreRaw &&
      (suggestedStoreRaw.isShowroom ||
        suggestedStoreRaw.locationType === "showroom" ||
        suggestedStoreRaw.supportsClickCollect === false)
    ) {
      setSuggestedStoreId(null);
    }
  }, [suggestedStoreId, suggestedStoreRaw]);
  const hasShowroom = selectedStore?.isShowroom ?? false;

  const value = useMemo(() => ({
    agencies: agenciesList, selectedId, selectedStore, setSelectedId, hasShowroom, loaded,
    suggestedStore, suggestedStoreId, setSuggestedStoreId,
  }), [agenciesList, selectedId, selectedStore, setSelectedId, hasShowroom, loaded, suggestedStore, suggestedStoreId, setSuggestedStoreId]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}
